import { Injectable, Signal, computed, inject } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { OrderService } from '../order.service';
import { ModalContentService } from '../modal-content/modal-content.service';
import { ModalContentTypes } from '../constants/modal-content-types';
import { Promotion } from '../types/promotion.type';
import { environment } from '../../../environments/environment';
import { SearchSuggestion, TypesenseSearchService } from '../typesense-search.service';
import { formatStringForURL } from '../common/utils';

/**
 * Shared facade for the Cart Promotions feature (DEV-9447).
 *
 * Exposes the derived promotion state and a single entry point to open the
 * promotions modal, so the side cart and the order summary page stay thin and
 * the feature can be enabled per client via `flows.order.cartPromotions`.
 */
@Injectable({ providedIn: 'root' })
export class PromotionsService {
  private orderService = inject(OrderService);
  private modalContentService = inject(ModalContentService);
  private typesenseService = inject(TypesenseSearchService);

  /** Feature flag — enable per white-label client in the environment file. */
  readonly enabled: boolean = environment.config?.flows?.order?.cartPromotions ?? false;

  /** Cart promotions present on the current order. */
  promotions: Signal<Promotion[]> = computed(() => this.orderService.order()?.promotions || []);

  /** Promotions currently applied to the order. */
  appliedPromotions: Signal<Promotion[]> = computed(() =>
    this.promotions().filter((p) => p.isApplied),
  );

  /** Whether the "See Available Promotions" entry point should be shown. */
  hasCartPromotions: Signal<boolean> = computed(() => this.enabled && this.promotions().length > 0);

  /**
   * Fetches the products a promotion can be filled with, as the same compact
   * cards used in search. Resolves by condition kind (DEV-9447):
   *   - `products`   → the exact products/variants the promotion targets;
   *   - `categories` → up to 12 products from those categories (hierarchical);
   *   - `productTags`→ up to 12 products carrying those tags.
   * Lazy — call only when the user expands a promotion's product list.
   */
  async getPromotionProducts(promotion: Promotion): Promise<SearchSuggestion[]> {
    const c = promotion.conditions || {};

    if (c.products?.length) {
      const ids = c.products.map((p) => p.productId);
      const variantIds = c.products.map((p) => p.variantId).filter((v) => v != null);
      return this.typesenseService.getProductSuggestionsByIds(ids, variantIds);
    }
    if (c.categories?.length) {
      return this.typesenseService.getProductSuggestionsByCategories(c.categories.map((cat) => cat.id));
    }
    if (c.productTags?.length) {
      return this.typesenseService.getProductSuggestionsByTags(c.productTags.map((t) => t.id));
    }
    return [];
  }

  /** True when a promotion can surface a product list to add (any item-level condition). */
  hasProductSuggestions(promotion: Promotion): boolean {
    const c = promotion.conditions || {};
    return !!(c.products?.length || c.categories?.length || c.productTags?.length);
  }

  /**
   * Shop link for a category-based promotion ("View all in {category}"), or null
   * when the promotion isn't category-based. Uses the first category (shop routes
   * a single category), matching how the header navigates: `/shop/{slug}`.
   */
  getPromotionCategoryLink(promotion: Promotion): { name: string; url: string } | null {
    const category = promotion.conditions?.categories?.[0];
    if (!category?.name) return null;
    return { name: category.name, url: `/shop/${formatStringForURL(category.name)}` };
  }

  /** Opens the available promotions modal. */
  openPromotionsModal(): NgbModalRef {
    return this.modalContentService.openModal(ModalContentTypes.AVAILABLE_PROMOTIONS, {
      title: 'Promotions',
      // The promotions modal renders its own headings (DEV-10937), so it opts out
      // of the shared carrot icon + title header.
      hideHeader: true,
      closeable: true,
      fullScreenOnMobile: true,
    });
  }
}
